<template>
  <teleport to="body">
    <div
      class="command_palette_overlay"
      v-if="modelValue"
      @click.self="close"
    >
      <div class="command_palette">
        <div class="command_palette_input_wrapper">
          <svg class="command_palette_icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.3-4.3"></path>
          </svg>
          <input
            ref="searchInput"
            v-model="query"
            type="text"
            class="command_palette_input"
            :placeholder="$t('commandPalette.placeholder')"
            @keydown="handleKeydown"
          />
          <span class="command_palette_shortcut">ESC</span>
        </div>

        <div class="command_palette_results" v-if="query.length > 0">
          <!-- Projects Section -->
          <div v-if="filteredProjects.length > 0" class="command_palette_section">
            <div class="command_palette_section_header">{{ $t('commandPalette.projects') }}</div>
            <div
              v-for="(project, index) in filteredProjects"
              :key="'project-' + project.id"
              :class="['command_palette_item', { 'command_palette_item_selected': selectedIndex === index }]"
              @click="selectProject(project)"
              @mouseenter="selectedIndex = index"
            >
              <svg class="command_palette_item_icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/>
              </svg>
              <div class="command_palette_item_content">
                <span class="command_palette_item_name">{{ project.name }}</span>
                <span class="command_palette_item_meta" v-if="project.artist">{{ project.artist }}</span>
              </div>
            </div>
          </div>

          <!-- Tracks Section -->
          <div v-if="filteredTracks.length > 0" class="command_palette_section">
            <div class="command_palette_section_header">{{ $t('commandPalette.tracks') }}</div>
            <div
              v-for="(track, index) in filteredTracks"
              :key="'track-' + track.id"
              :class="['command_palette_item', { 'command_palette_item_selected': selectedIndex === filteredProjects.length + index }]"
              @click="selectTrack(track)"
              @mouseenter="selectedIndex = filteredProjects.length + index"
            >
              <svg class="command_palette_item_icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 18V5l12-2v13"/>
                <circle cx="6" cy="18" r="3"/>
                <circle cx="18" cy="16" r="3"/>
              </svg>
              <div class="command_palette_item_content">
                <span class="command_palette_item_name">{{ track.name }}</span>
                <span class="command_palette_item_meta" v-if="track.artist">{{ track.artist }}</span>
              </div>
            </div>
          </div>

          <!-- Pages Section -->
          <div v-if="filteredPages.length > 0" class="command_palette_section">
            <div class="command_palette_section_header">{{ $t('commandPalette.pages') }}</div>
            <div
              v-for="(page, index) in filteredPages"
              :key="'page-' + page.path"
              :class="['command_palette_item', { 'command_palette_item_selected': selectedIndex === filteredProjects.length + filteredTracks.length + index }]"
              @click="navigateTo(page.path)"
              @mouseenter="selectedIndex = filteredProjects.length + filteredTracks.length + index"
            >
              <i :class="['command_palette_item_icon', page.icon]"></i>
              <div class="command_palette_item_content">
                <span class="command_palette_item_name">{{ getPageName(page) }}</span>
              </div>
            </div>
          </div>

          <!-- No Results -->
          <div v-if="filteredProjects.length === 0 && filteredTracks.length === 0 && filteredPages.length === 0" class="command_palette_empty">
            {{ $t('commandPalette.noResults') }}
          </div>
        </div>

        <!-- Quick Links when empty -->
        <div class="command_palette_results" v-else>
          <div class="command_palette_section">
            <div class="command_palette_section_header">{{ $t('commandPalette.quickLinks') }}</div>
            <div
              v-for="(page, index) in pages"
              :key="'quick-' + page.path"
              :class="['command_palette_item', { 'command_palette_item_selected': selectedIndex === index }]"
              @click="navigateTo(page.path)"
              @mouseenter="selectedIndex = index"
            >
              <i :class="['command_palette_item_icon', page.icon]"></i>
              <div class="command_palette_item_content">
                <span class="command_palette_item_name">{{ getPageName(page) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import storageService from '@/scripts/services/StorageService'

export default {
  name: 'CommandPalette',
  props: {
    modelValue: { type: Boolean, required: true }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      query: '',
      selectedIndex: 0,
      pages: [
        { labelKey: 'commandPalette.page.home', path: '/', icon: 'bi bi-house' },
        { labelKey: 'commandPalette.page.account', path: '/settings?tab=account', icon: 'bi bi-person' },
        { labelKey: 'commandPalette.page.agents', path: '/settings?tab=agents', icon: 'bi bi-cloud-arrow-down' },
        { labelKey: 'commandPalette.page.links', path: '/settings?tab=links', icon: 'bi bi-link' },
        { labelKey: 'commandPalette.page.settings', path: '/settings', icon: 'bi bi-gear' },
        { labelKey: 'commandPalette.page.help', path: '/help', icon: 'bi bi-question-circle' }
      ]
    }
  },
  computed: {
    searchResults() {
      return storageService.search(this.query)
    },
    filteredProjects() {
      return this.searchResults.projects
    },
    filteredTracks() {
      return this.searchResults.tracks
    },
    filteredPages() {
      if (!this.query) return []
      const q = this.query.toLowerCase()
      return this.pages.filter(p => this.getPageName(p).toLowerCase().includes(q))
    },
    totalResults() {
      if (!this.query) return this.pages.length
      return this.filteredProjects.length + this.filteredTracks.length + this.filteredPages.length
    }
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.query = ''
        this.selectedIndex = 0
        this.$nextTick(() => {
          this.$refs.searchInput?.focus()
        })
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    },
    query() {
      this.selectedIndex = 0
    }
  },
  methods: {
    getPageName(page) {
      return this.$t(page.labelKey)
    },
    close() {
      this.$emit('update:modelValue', false)
    },
    handleKeydown(e) {
      switch (e.key) {
        case 'Escape':
          e.preventDefault()
          this.close()
          break
        case 'ArrowDown':
          e.preventDefault()
          if (this.selectedIndex < this.totalResults - 1) {
            this.selectedIndex++
          }
          break
        case 'ArrowUp':
          e.preventDefault()
          if (this.selectedIndex > 0) {
            this.selectedIndex--
          }
          break
        case 'Enter':
          e.preventDefault()
          this.selectCurrent()
          break
      }
    },
    selectCurrent() {
      if (this.totalResults === 0) return

      // When no query, selecting from quick links
      if (!this.query) {
        this.navigateTo(this.pages[this.selectedIndex].path)
        return
      }

      if (this.selectedIndex < this.filteredProjects.length) {
        this.selectProject(this.filteredProjects[this.selectedIndex])
      } else if (this.selectedIndex < this.filteredProjects.length + this.filteredTracks.length) {
        const trackIndex = this.selectedIndex - this.filteredProjects.length
        this.selectTrack(this.filteredTracks[trackIndex])
      } else {
        const pageIndex = this.selectedIndex - this.filteredProjects.length - this.filteredTracks.length
        this.navigateTo(this.filteredPages[pageIndex].path)
      }
    },
    selectProject(project) {
      this.close()
      this.$router.push(`/project/${project.id}`)
    },
    selectTrack(track) {
      this.close()
      this.$router.push(`/track/${track.id}`)
    },
    navigateTo(path) {
      this.close()
      this.$router.push(path)
    }
  }
}
</script>

<style scoped>
.command_palette_overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 15vh;
  z-index: 9999;
}

.command_palette {
  width: 100%;
  max-width: 560px;
  background: rgba(33, 33, 33, 0.95);
  border-radius: 12px;
  box-shadow: 0 16px 70px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(47, 47, 47, 0.8);
  overflow: hidden;
}

.command_palette_input_wrapper {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(47, 47, 47, 0.8);
  gap: 0.75rem;
}

.command_palette_icon {
  color: #a3a3a3;
  flex-shrink: 0;
}

.command_palette_input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 1rem;
  color: #e4e4e4;
  font-weight: 500;
}

.command_palette_input::placeholder {
  color: #666;
}

.command_palette_shortcut {
  font-size: 0.75rem;
  color: #666;
  background: rgba(47, 47, 47, 0.8);
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
}

.command_palette_results {
  max-height: 400px;
  overflow-y: auto;
}

.command_palette_section {
  padding: 0.5rem;
}

.command_palette_section_header {
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.5rem 0.75rem;
}

.command_palette_item {
  display: flex;
  align-items: center;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  gap: 0.75rem;
  transition: background-color 100ms ease;
}

.command_palette_item:hover,
.command_palette_item_selected {
  background: rgba(47, 47, 47, 0.6);
}

.command_palette_item_icon {
  color: #a3a3a3;
  flex-shrink: 0;
}

.command_palette_item_content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.command_palette_item_name {
  font-weight: 500;
  color: #e4e4e4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.command_palette_item_meta {
  font-size: 0.8rem;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.command_palette_empty {
  padding: 2rem;
  text-align: center;
  color: #666;
}

.command_palette_hint {
  padding: 2rem;
  text-align: center;
  color: #666;
}
</style>
